import { SHORTCUTS, shortcutLabel } from "../hooks/use-shortcuts";
import { ConnectionSettings } from "./ConnectionSettings";
import { Icon, type IconName } from "./Icon";
import { ProfileButton } from "./ProfileButton";
import { ThemeToggle } from "./ThemeToggle";

type Panel = "files" | "side" | "bottom";

interface ActivityBarProps {
  panels: Record<Panel, boolean>;
  onTogglePanel: (panel: Panel) => void;
  onCommandPalette: () => void;
  roomHasPeople: boolean;
  onConnectionSaved: () => void;
}

const VIEWS: { panel: Panel; label: string; title: string; icon: IconName }[] = [
  {
    panel: "files",
    label: "Files",
    title: `Explorer (${shortcutLabel(SHORTCUTS.toggleFiles)})`,
    icon: "files",
  },
  {
    panel: "bottom",
    label: "Terminal",
    title: `Terminal (${shortcutLabel(SHORTCUTS.toggleTerminal)})`,
    icon: "terminal",
  },
];

/**
 * VS Code's activity bar: the left-hand views at the top (each toggles its panel), you and
 * settings at the bottom. People & chat opens on the right, so its toggle is in the title bar's
 * right corner, above it.
 */
export function ActivityBar({
  panels,
  onTogglePanel,
  onCommandPalette,
  roomHasPeople,
  onConnectionSaved,
}: ActivityBarProps) {
  return (
    <nav className="activity-bar" aria-label="Views">
      {VIEWS.map((view) => (
        <button
          key={view.panel}
          type="button"
          id={`toggle-${view.panel}`}
          className="activity-item"
          aria-label={view.label}
          aria-pressed={panels[view.panel]}
          title={view.title}
          onClick={() => onTogglePanel(view.panel)}
          // Escape closes the panel even before focus has moved into it.
          onKeyDown={(e) => {
            if (e.key === "Escape" && panels[view.panel]) onTogglePanel(view.panel);
          }}
        >
          <Icon name={view.icon} size={22} />
        </button>
      ))}
      <span className="activity-spacer" />
      <ProfileButton />
      <ConnectionSettings roomHasPeople={roomHasPeople} onSaved={onConnectionSaved} />
      <ThemeToggle />
      <button
        type="button"
        className="activity-item"
        aria-label="Command palette"
        title={`Command palette (${shortcutLabel(SHORTCUTS.commandPalette)})`}
        onClick={onCommandPalette}
      >
        <Icon name="gear" size={20} />
      </button>
    </nav>
  );
}
